import React, { useMemo } from "react";
import { useFragment } from "react-relay";
import { graphql } from "relay-runtime";
import { AHIcon, AHText, Box, TouchableBox } from "arkh";
import { TeamScheduleSectionHeader_teamSchedule$key } from "./__generated__/TeamScheduleSectionHeader_teamSchedule.graphql";
import { TeamScheduleSectionHeaderSummary_teamSchedule$key } from "./__generated__/TeamScheduleSectionHeaderSummary_teamSchedule.graphql";
import { View } from "react-native";

import { TeamScheduleSectionHeaderSummaryOccasions } from "./TeamScheduleSectionHeaderSummaryOccasions";
import { SharedValue } from "react-native-reanimated";

export const TeamScheduleSectionHeaderHeight = 105;
export const TeamScheduleSectionHeaderSummaryHeight = 45;

export function TeamScheduleSectionHeader(props: {
  title: string;
  onPressHistory(): void;
  onPress(): void;
  isFree: boolean;
  color: string;
  teamSchedule: TeamScheduleSectionHeader_teamSchedule$key;
  historyTitle: React.ReactNode;
  backgroundColor: string;
}) {
  const teamSchedule = useFragment(
    graphql`
      fragment TeamScheduleSectionHeader_teamSchedule on TeamSchedule {
        label
        ...TeamScheduleSectionHeaderSummary_teamSchedule
      }
    `,
    props.teamSchedule
  );

  return (
    <SectionHeader
      {...props}
      historyTitle={props.historyTitle}
      label={teamSchedule.label}
      summary={
        <TeamScheduleSectionHeaderSummary
          onPress={props.onPress}
          title={props.title}
          teamSchedule={teamSchedule}
        />
      }
    />
  );
}

function SectionHeader(props: {
  title: string;
  label: string;
  historyTitle: React.ReactNode;
  onPressHistory(): void;
  isFree: boolean;
  color: string;

  backgroundColor: string;
  summary?: React.ReactNode;
}) {
  return (
    <View
      style={{
        height: TeamScheduleSectionHeaderHeight,
      }}
    >
      <Box style={{ flex: 1 }} spaceHorizontal="medium">
        {props.summary}
        <Box
          style={{
            flex: 1,
            flexDirection: "row",
            justifyContent: "space-between",
            alignItems: "flex-start",
            zIndex: 0,
          }}
          spaceHorizontal="medium"
          color="surface"
          cornerRadiusBottomLeft={props.isFree ? "medium" : "none"}
          cornerRadiusBottomRight={props.isFree ? "medium" : "none"}
        >
          <AHText type="title">{props.label}</AHText>
          <TouchableBox
            borderColor="border-primary"
            cornerRadius="large"
            style={{
              borderWidth: 1,
              flexDirection: "row",
              alignItems: "center",
              justifyContent: "space-between",
            }}
            spaceHorizontal="medium"
            spaceVertical="small"
            spaceRight="small"
            onPress={props.onPressHistory}
          >
            <AHText type="info-small" color="text-secondary">
              {props.historyTitle}
            </AHText>
            <AHIcon
              spaceLeft="small"
              type="chevron-right"
              size="xsmall"
              color="text-secondary"
            />
          </TouchableBox>
        </Box>
      </Box>
    </View>
  );
}

TeamScheduleSectionHeader.height = TeamScheduleSectionHeaderHeight;

export function TeamScheduleSectionHeaderSummary(props: {
  teamSchedule: TeamScheduleSectionHeaderSummary_teamSchedule$key;
  title: string;
  onPress(): void;
  stickyIndex?: SharedValue<number>;
  index?: number;
  isFree?: boolean;
}) {
  const { teamSchedule: _teamSchedule, ...rest } = props;
  const teamSchedule = useFragment(
    graphql`
      fragment TeamScheduleSectionHeaderSummary_teamSchedule on TeamSchedule {
        id

        events(includeInlineRoutines: true) {
          ... on RoutineOccasion {
            __typename
            ...TeamScheduleSectionHeaderSummaryOccasions_teamSchedule
          }
          ... on TeamScheduleInlineRoutineOccasion {
            __typename
            taskExecution {
              routineOccasion {
                ...TeamScheduleSectionHeaderSummaryOccasions_teamSchedule
              }
            }
          }
        }
      }
    `,
    _teamSchedule
  );

  const occasions = useMemo(
    () =>
      teamSchedule.events.flatMap((event) =>
        event.__typename === "RoutineOccasion"
          ? [event]
          : event.__typename === "TeamScheduleInlineRoutineOccasion"
            ? [event.taskExecution.routineOccasion]
            : []
      ),
    [teamSchedule.events]
  );
  return (
    <TeamScheduleSectionHeaderSummaryOccasions
      occasions={occasions}
      {...rest}
    />
  );
}

TeamScheduleSectionHeaderSummary.height =
  TeamScheduleSectionHeaderSummaryHeight;
